'use client';

import { useCallback, type MouseEvent } from 'react';
import { useGlobalContextMenu } from '../../hooks/useGlobalContextMenu';
import { GlobalMenuItemsTypeEnum } from '../../store/globalContextMenuStore';
import { useMainContentTabs, useSidebarState } from '../../store/layoutStore';
import type { AssetsTabFilterState } from '../../store/tab/assetsTabScopedFilterStore';
import type { FindingsTabFilterState } from '../../store/tab/findingsTabScopedFilterStore';
import type { SignalsTabFilterState } from '../../store/tab/signalsTabScopedFilterStore';
import { getActiveTabScopeKey } from '../../store/tab/active-tab-scope-key';
import { tabScopedStore } from '../../store/tab/tabScopedStore';
import type { TracesTabFilterState } from '../../store/tab/tracesTabScopedFilterStore';
import { sidebarTabMapping } from '../layout/utils';

export type HomeListDestination =
  | {
      list: 'findings';
      filters?: Partial<
        Pick<
          FindingsTabFilterState,
          | 'createdStartDate'
          | 'createdSince'
          | 'severityFilter'
          | 'publishedFilter'
        >
      >;
    }
  | {
      list: 'signals';
      filters?: Partial<
        Pick<
          SignalsTabFilterState,
          'createdStartDate' | 'createdSince' | 'stageFilter' | 'statusFilter'
        >
      >;
    }
  | {
      list: 'assets';
      filters?: Partial<
        Pick<AssetsTabFilterState, 'createdStartDate' | 'createdSince'>
      >;
    }
  | {
      list: 'executions';
      filters?: Partial<
        Pick<TracesTabFilterState, 'statusFilter' | 'createdSince'>
      >;
    };

type HomeListFilterBaseline = {
  [List in HomeListDestination['list']]: Required<
    NonNullable<Extract<HomeListDestination, { list: List }>['filters']>
  >;
};

const HOME_LIST_FILTER_BASELINE: HomeListFilterBaseline = {
  findings: {
    createdStartDate: null,
    createdSince: null,
    severityFilter: [],
    publishedFilter: 'ALL',
  },
  signals: {
    createdStartDate: null,
    createdSince: null,
    stageFilter: [],
    statusFilter: [],
  },
  assets: { createdStartDate: null, createdSince: null },
  executions: { statusFilter: [], createdSince: null },
};

export function useOpenListFromHome({
  applicationId,
}: {
  applicationId: string;
}) {
  const { navigateInActiveTab } = useMainContentTabs();
  const { setSidebarTab } = useSidebarState();
  const { openContextMenu } = useGlobalContextMenu();

  const applyFiltersToActiveTab = useCallback(
    (destination: HomeListDestination) => {
      const scopeKey = getActiveTabScopeKey(applicationId);

      if (scopeKey) {
        tabScopedStore.getState().updateEntry(scopeKey, {
          ...HOME_LIST_FILTER_BASELINE[destination.list],
          ...destination.filters,
        });
      }
    },
    [applicationId],
  );

  const openList = useCallback(
    (destination: HomeListDestination) => {
      setSidebarTab(destination.list);
      navigateInActiveTab(sidebarTabMapping[destination.list]);
      applyFiltersToActiveTab(destination);
    },
    [applyFiltersToActiveTab, navigateInActiveTab, setSidebarTab],
  );

  const openListContextMenu = useCallback(
    (event: MouseEvent<HTMLElement>, destination: HomeListDestination) => {
      openContextMenu({
        event,
        items: [
          {
            type: GlobalMenuItemsTypeEnum.OPEN_IN_NEW_TAB,
            tab: sidebarTabMapping[destination.list],
            onClick: () => applyFiltersToActiveTab(destination),
          },
        ],
      });
    },
    [applyFiltersToActiveTab, openContextMenu],
  );

  return { openList, openListContextMenu };
}
